import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { useQuery } from 'convex/react';
import { api } from '../../convex/_generated/api';
import {
    Bell,
    Send,
    Users,
    CheckCircle,
    AlertCircle,
    Info,
    History,
    Clock
} from 'lucide-react';
import Card from '../components/Card';
import Button from '../components/Button';
import Input from '../components/Input';
import { sendCustomNotification } from '../utils/api';
import { formatTimeAgo } from '../utils/helpers';
import toast from 'react-hot-toast';

const NOTIFICATION_CATEGORIES = [
    { value: 'posts', label: 'Posts', description: 'General post-related notifications' },
    { value: 'comments', label: 'Comments', description: 'Comment and reply notifications' },
    { value: 'follows', label: 'Follows', description: 'New follower notifications' },
    { value: 'messages', label: 'Messages', description: 'Direct message notifications' },
    { value: 'mentions', label: 'Mentions', description: 'User mention notifications' },
    { value: 'liveGames', label: 'Live Games', description: 'Basketball game notifications' },
    { value: 'communityPosts', label: 'Community Posts', description: 'Community-related posts' }
];

const TARGETING_MODES = [
    { value: 'all', label: 'All Users', description: 'Send to all users' },
    { value: 'verified', label: 'Verified Users', description: 'Send to all verified users' },
    { value: 'accountType', label: 'Specific Account Type', description: 'Target users by account type' },
    { value: 'specificUsers', label: 'Specific Users', description: 'Target specific user IDs' }
];

const ACCOUNT_TYPES = [
    { value: 'verified', label: 'Verified' },
    { value: 'reporter', label: 'Reporter' },
    { value: 'community', label: 'Community' },
    { value: 'special', label: 'Special' },
    { value: 'super', label: 'Super' }
];

export default function NotificationsManager() {
    const [formData, setFormData] = useState({
        title: '',
        body: '',
        category: 'posts',
        targeting: {
            mode: 'all',
            accountType: '',
            userIds: ''
        }
    });

    const [result, setResult] = useState(null);
    const [showHistory, setShowHistory] = useState(false);

    // Fetch notification history
    const notificationLogs = useQuery(api.activityLogs.getNotificationLogs, { limit: 50 });

    // Send notification mutation
    const sendMutation = useMutation({
        mutationFn: sendCustomNotification,
        onSuccess: (data) => {
            toast.success('Notification sent successfully');
            setResult(data.data);
            // Reset form
            setFormData({
                title: '',
                body: '',
                category: 'posts',
                targeting: {
                    mode: 'all',
                    accountType: '',
                    userIds: ''
                }
            });
        },
        onError: (error) => toast.error(error.message),
    });

    const handleSubmit = (e) => {
        e.preventDefault();

        // Validate
        if (!formData.title.trim()) {
            toast.error('Title is required');
            return;
        }

        if (!formData.body.trim()) {
            toast.error('Body is required');
            return;
        }

        if (formData.targeting.mode === 'accountType' && !formData.targeting.accountType) {
            toast.error('Please select an account type');
            return;
        }

        if (formData.targeting.mode === 'specificUsers' && !formData.targeting.userIds.trim()) {
            toast.error('Please enter user IDs');
            return;
        }

        // Prepare data
        const data = {
            title: formData.title.trim(),
            body: formData.body.trim(),
            category: formData.category,
            targeting: {
                mode: formData.targeting.mode
            }
        };

        // Add optional targeting fields
        if (formData.targeting.mode === 'accountType') {
            data.targeting.accountType = formData.targeting.accountType;
        }

        if (formData.targeting.mode === 'specificUsers') {
            // Parse user IDs (comma or newline separated)
            const userIds = formData.targeting.userIds
                .split(/[\n,]/)
                .map(id => id.trim())
                .filter(id => id.length > 0);
            data.targeting.userIds = userIds;
        }

        sendMutation.mutate(data);
    };

    const selectedCategory = NOTIFICATION_CATEGORIES.find(c => c.value === formData.category);
    const selectedTargeting = TARGETING_MODES.find(m => m.value === formData.targeting.mode);

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex justify-between items-start">
                <div>
                    <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                        <Bell className="w-7 h-7" />
                        Send Custom Notifications
                    </h1>
                    <p className="text-gray-600 mt-1">
                        Send targeted push notifications to users. Notifications respect user settings for the selected category.
                    </p>
                </div>
                <Button
                    variant="secondary"
                    onClick={() => setShowHistory(!showHistory)}
                    className="flex items-center gap-2"
                >
                    <History className="w-4 h-4" />
                    {showHistory ? 'Hide' : 'Show'} History
                </Button>
            </div>

            {/* Important Notice */}
            <Card className="bg-blue-50 border-blue-200">
                <div className="p-4 flex gap-3">
                    <Info className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                    <div className="text-sm text-blue-900">
                        <p className="font-medium mb-1">Important Notes:</p>
                        <ul className="list-disc list-inside space-y-1 text-blue-800">
                            <li>Notifications are sent as push-only (no in-app notification record)</li>
                            <li>Users who have disabled the selected category will NOT receive the notification</li>
                            <li>Only users with valid push tokens will receive notifications</li>
                            <li>The notification will be marked as an admin announcement</li>
                        </ul>
                    </div>
                </div>
            </Card>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Form */}
                <div className="lg:col-span-2">
                    <Card>
                        <form onSubmit={handleSubmit} className="p-6 space-y-6">
                            {/* Notification Content */}
                            <div className="space-y-4">
                                <h3 className="font-semibold text-gray-900 border-b pb-2">
                                    Notification Content
                                </h3>

                                <Input
                                    label="Title"
                                    value={formData.title}
                                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                                    required
                                    maxLength={100}
                                    placeholder="e.g., Important Update"
                                    helperText={`${formData.title.length}/100 characters`}
                                />

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Body <span className="text-red-500">*</span>
                                    </label>
                                    <textarea
                                        value={formData.body}
                                        onChange={(e) => setFormData({ ...formData, body: e.target.value })}
                                        required
                                        maxLength={500}
                                        rows={4}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                        placeholder="Enter the notification message..."
                                    />
                                    <p className="text-xs text-gray-500 mt-1">
                                        {formData.body.length}/500 characters
                                    </p>
                                </div>
                            </div>

                            {/* Category Selection */}
                            <div className="space-y-4">
                                <h3 className="font-semibold text-gray-900 border-b pb-2">
                                    Notification Category
                                </h3>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Category <span className="text-red-500">*</span>
                                    </label>
                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                                        {NOTIFICATION_CATEGORIES.map((category) => (
                                            <label
                                                key={category.value}
                                                className={`flex items-start gap-3 p-3 border rounded-lg cursor-pointer transition-colors ${
                                                    formData.category === category.value
                                                        ? 'border-blue-500 bg-blue-50'
                                                        : 'border-gray-300 hover:border-gray-400'
                                                }`}
                                            >
                                                <input
                                                    type="radio"
                                                    name="category"
                                                    value={category.value}
                                                    checked={formData.category === category.value}
                                                    onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                                                    className="mt-1"
                                                />
                                                <div className="flex-1">
                                                    <div className="font-medium text-sm text-gray-900">
                                                        {category.label}
                                                    </div>
                                                    <div className="text-xs text-gray-500 mt-0.5">
                                                        {category.description}
                                                    </div>
                                                </div>
                                            </label>
                                        ))}
                                    </div>
                                    <p className="text-xs text-gray-500 mt-2">
                                        Users who have disabled this category in their settings will not receive the notification.
                                    </p>
                                </div>
                            </div>

                            {/* Targeting */}
                            <div className="space-y-4">
                                <h3 className="font-semibold text-gray-900 border-b pb-2">
                                    Target Audience
                                </h3>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Targeting Mode <span className="text-red-500">*</span>
                                    </label>
                                    <div className="space-y-2">
                                        {TARGETING_MODES.map((mode) => (
                                            <label
                                                key={mode.value}
                                                className={`flex items-start gap-3 p-3 border rounded-lg cursor-pointer transition-colors ${
                                                    formData.targeting.mode === mode.value
                                                        ? 'border-blue-500 bg-blue-50'
                                                        : 'border-gray-300 hover:border-gray-400'
                                                }`}
                                            >
                                                <input
                                                    type="radio"
                                                    name="targetingMode"
                                                    value={mode.value}
                                                    checked={formData.targeting.mode === mode.value}
                                                    onChange={(e) => setFormData({
                                                        ...formData,
                                                        targeting: {
                                                            ...formData.targeting,
                                                            mode: e.target.value
                                                        }
                                                    })}
                                                    className="mt-1"
                                                />
                                                <div className="flex-1">
                                                    <div className="font-medium text-sm text-gray-900">
                                                        {mode.label}
                                                    </div>
                                                    <div className="text-xs text-gray-500 mt-0.5">
                                                        {mode.description}
                                                    </div>
                                                </div>
                                            </label>
                                        ))}
                                    </div>
                                </div>

                                {/* Account Type Selection */}
                                {formData.targeting.mode === 'accountType' && (
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                            Account Type <span className="text-red-500">*</span>
                                        </label>
                                        <select
                                            value={formData.targeting.accountType}
                                            onChange={(e) => setFormData({
                                                ...formData,
                                                targeting: {
                                                    ...formData.targeting,
                                                    accountType: e.target.value
                                                }
                                            })}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                                        >
                                            <option value="">Select account type...</option>
                                            {ACCOUNT_TYPES.map((type) => (
                                                <option key={type.value} value={type.value}>
                                                    {type.label}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                )}

                                {/* Specific User IDs */}
                                {formData.targeting.mode === 'specificUsers' && (
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            User IDs <span className="text-red-500">*</span>
                                        </label>
                                        <textarea
                                            value={formData.targeting.userIds}
                                            onChange={(e) => setFormData({
                                                ...formData,
                                                targeting: {
                                                    ...formData.targeting,
                                                    userIds: e.target.value
                                                }
                                            })}
                                            rows={4}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent font-mono text-sm"
                                            placeholder="Enter user IDs (one per line or comma-separated)"
                                        />
                                        <p className="text-xs text-gray-500 mt-1">
                                            Enter Convex user IDs, one per line or comma-separated
                                        </p>
                                    </div>
                                )}
                            </div>

                            {/* Submit */}
                            <div className="flex justify-end gap-3 pt-4 border-t">
                                <Button
                                    type="button"
                                    variant="secondary"
                                    onClick={() => {
                                        setFormData({
                                            title: '',
                                            body: '',
                                            category: 'posts',
                                            targeting: {
                                                mode: 'all',
                                                accountType: '',
                                                userIds: ''
                                            }
                                        });
                                        setResult(null);
                                    }}
                                >
                                    Reset
                                </Button>
                                <Button
                                    type="submit"
                                    disabled={sendMutation.isPending}
                                    className="flex items-center gap-2"
                                >
                                    <Send className="w-4 h-4" />
                                    {sendMutation.isPending ? 'Sending...' : 'Send Notification'}
                                </Button>
                            </div>
                        </form>
                    </Card>
                </div>

                {/* Preview & Info */}
                <div className="space-y-6">
                    {/* Preview */}
                    <Card>
                        <div className="p-4 border-b border-gray-200">
                            <h3 className="font-semibold text-gray-900">Preview</h3>
                        </div>
                        <div className="p-4 space-y-4">
                            {/* Phone mockup */}
                            <div className="bg-gray-900 rounded-2xl p-3 shadow-lg">
                                <div className="bg-white rounded-lg p-3 space-y-2">
                                    <div className="flex items-start gap-2">
                                        <div className="w-8 h-8 bg-blue-500 rounded-lg flex items-center justify-center flex-shrink-0">
                                            <Bell className="w-4 h-4 text-white" />
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <div className="text-xs text-gray-500 mb-0.5">LBA</div>
                                            <div className="font-semibold text-sm text-gray-900 break-words">
                                                {formData.title || 'Notification Title'}
                                            </div>
                                            <div className="text-sm text-gray-600 mt-1 break-words">
                                                {formData.body || 'Notification body will appear here...'}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Details */}
                            <div className="space-y-2 text-sm">
                                <div>
                                    <span className="text-gray-500">Category:</span>
                                    <span className="ml-2 font-medium text-gray-900">
                                        {selectedCategory?.label}
                                    </span>
                                </div>
                                <div>
                                    <span className="text-gray-500">Target:</span>
                                    <span className="ml-2 font-medium text-gray-900">
                                        {selectedTargeting?.label}
                                    </span>
                                </div>
                            </div>
                        </div>
                    </Card>

                    {/* Result */}
                    {result && (
                        <Card>
                            <div className="p-4 border-b border-gray-200">
                                <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                                    <CheckCircle className="w-5 h-5 text-green-600" />
                                    Send Result
                                </h3>
                            </div>
                            <div className="p-4 space-y-3">
                                <div className="flex items-center justify-between">
                                    <span className="text-sm text-gray-600">Targeted Users</span>
                                    <span className="font-semibold text-gray-900">{result.targetedUsers}</span>
                                </div>
                                <div className="flex items-center justify-between">
                                    <span className="text-sm text-gray-600">Successfully Sent</span>
                                    <span className="font-semibold text-green-600">{result.sentCount}</span>
                                </div>
                                <div className="flex items-center justify-between">
                                    <span className="text-sm text-gray-600">Skipped</span>
                                    <span className="font-semibold text-gray-600">{result.skippedCount}</span>
                                </div>
                                <div className="pt-2 border-t text-xs text-gray-500">
                                    Users may be skipped if they disabled this notification category or have no push tokens.
                                </div>
                            </div>
                        </Card>
                    )}

                    {/* Guidelines */}
                    <Card>
                        <div className="p-4 border-b border-gray-200">
                            <h3 className="font-semibold text-gray-900">Best Practices</h3>
                        </div>
                        <div className="p-4 space-y-2 text-sm text-gray-600">
                            <div className="flex gap-2">
                                <AlertCircle className="w-4 h-4 text-orange-500 flex-shrink-0 mt-0.5" />
                                <span>Keep titles short and clear (under 50 chars)</span>
                            </div>
                            <div className="flex gap-2">
                                <AlertCircle className="w-4 h-4 text-orange-500 flex-shrink-0 mt-0.5" />
                                <span>Make the message actionable and relevant</span>
                            </div>
                            <div className="flex gap-2">
                                <AlertCircle className="w-4 h-4 text-orange-500 flex-shrink-0 mt-0.5" />
                                <span>Choose the appropriate category to respect user preferences</span>
                            </div>
                            <div className="flex gap-2">
                                <AlertCircle className="w-4 h-4 text-orange-500 flex-shrink-0 mt-0.5" />
                                <span>Test with specific users before sending to all</span>
                            </div>
                        </div>
                    </Card>
                </div>
            </div>

            {/* History Section */}
            {showHistory && (
                <Card>
                    <div className="p-4 border-b border-gray-200">
                        <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                            <History className="w-5 h-5" />
                            Notification History
                        </h3>
                    </div>
                    <div className="divide-y divide-gray-200">
                        {!notificationLogs ? (
                            <div className="p-8 text-center text-gray-500">Loading...</div>
                        ) : notificationLogs.length === 0 ? (
                            <div className="p-8 text-center text-gray-500">No notifications sent yet</div>
                        ) : (
                            notificationLogs.map((log) => (
                                <div key={log._id} className="p-4 hover:bg-gray-50">
                                    <div className="flex items-start justify-between gap-4">
                                        <div className="flex-1 min-w-0">
                                            <div className="font-medium text-gray-900 truncate">
                                                {log.details?.metadata?.title || 'Untitled'}
                                            </div>
                                            <div className="text-sm text-gray-600 mt-1">
                                                Category: <span className="font-medium">{log.details?.metadata?.category}</span>
                                                {' • '}
                                                Mode: <span className="font-medium">{log.details?.metadata?.targetingMode}</span>
                                            </div>
                                            <div className="flex items-center gap-4 mt-2 text-xs text-gray-500">
                                                <span className="flex items-center gap-1">
                                                    <Users className="w-3 h-3" />
                                                    {log.details?.metadata?.targetedUsers || 0} targeted
                                                </span>
                                                <span className="flex items-center gap-1">
                                                    <CheckCircle className="w-3 h-3 text-green-600" />
                                                    {log.details?.metadata?.sentCount || 0} sent
                                                </span>
                                                <span className="text-gray-400">
                                                    {log.details?.metadata?.skippedCount || 0} skipped
                                                </span>
                                            </div>
                                        </div>
                                        <div className="text-right flex-shrink-0">
                                            <div className="text-xs text-gray-500 flex items-center gap-1">
                                                <Clock className="w-3 h-3" />
                                                {formatTimeAgo(log.timestamp)}
                                            </div>
                                            <div className="text-xs text-gray-500 mt-1">
                                                by {log.adminEmail}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ))
                        )}
                    </div>
                </Card>
            )}
        </div>
    );
}

